/**
 * Puts keyboard focus back where it came from when a dialog or sheet closes.
 *
 * Radix restores focus to a `DialogTrigger`, but every overlay in this app is
 * *controlled* — opened by setting state from a button that is not a Trigger,
 * because the same dialog is opened from several places and half of them open
 * it in "edit" mode with a row attached. With no Trigger to return to, Radix
 * drops focus on `<body>`: press Escape on the budget editor and your next Tab
 * starts again from the skip link, at the top of the page.
 *
 * The origin is recovered from a short history of recently focused elements
 * rather than from Radix's own `onOpenAutoFocus`, which does not fire reliably
 * for a controlled overlay — it fired for the budget dialog and never for the
 * subscription one. A `focusin` listener cannot be missed the same way, and it
 * also makes nesting work: a dialog opened from inside a sheet returns to the
 * button in the sheet, not to whatever was focused before the sheet opened.
 */
import { useCallback } from 'react'

/** Most recent last. Short, because only the tail is ever read. */
const history: HTMLElement[] = []
const HISTORY_LIMIT = 10
let listening = false

function remember(element: HTMLElement) {
  const existing = history.indexOf(element)
  if (existing !== -1) history.splice(existing, 1)
  history.push(element)
  if (history.length > HISTORY_LIMIT) history.shift()
}

function startListening() {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target
      if (target instanceof HTMLElement && target !== document.body) remember(target)
    },
    true,
  )
}

/**
 * The most recently focused element that is still on the page and outside the
 * overlay that is closing. Walking backwards handles the nested case.
 */
function originFor(content: Element | null): HTMLElement | null {
  for (let index = history.length - 1; index >= 0; index--) {
    const candidate = history[index]
    if (!candidate.isConnected) continue
    if (content && content.contains(candidate)) continue
    // Never return focus into another overlay that has since been dismissed.
    const overlay = candidate.closest('[data-slot="dialog-content"], [data-slot="sheet-content"]')
    if (overlay && !overlay.isConnected) continue
    return candidate
  }
  return null
}

export interface ReturnFocusHandlers {
  /** Spread onto a Radix `Content`. */
  onCloseAutoFocus: (event: Event) => void
}

export function useReturnFocus(onCloseAutoFocus?: (event: Event) => void): ReturnFocusHandlers {
  startListening()

  const handleCloseAutoFocus = useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event)
      if (event.defaultPrevented) return

      const content = event.currentTarget instanceof Element ? event.currentTarget : null
      const target = originFor(content)
      if (!target) return

      event.preventDefault()
      target.focus()
    },
    [onCloseAutoFocus],
  )

  return { onCloseAutoFocus: handleCloseAutoFocus }
}
